import { useAppDispatch } from "@/app/hooks";
import { getActiveFieldLabelList } from "@/shared/utils";
import { updatePropertyFieldRule } from "@features/survey/surveySlice";
import { useUpdatePropertyMutation } from "@services/survey-properties";
import { DetailedSurvey } from "@survey-types/survey.core.types";
import { Property } from "@survey-types/survey.property.types";
import { updateProperty as updatePropertyAction } from "@survey/surveySlice";
import React, { useState } from "react";
import { usePropertyFieldUpdate } from "./usePropertyFieldUpdate";

interface FocusedField {
  key: string;
  value: string;
}

/**
 * Shared hook for property form fields.
 *
 * Consolidates:
 *  - A single useUpdatePropertyMutation instance per form
 *  - Memoized activeFieldRuleList (was recomputed on every render)
 *  - focusedField state (replaces the unused full-property copy + clearedDashFields set)
 *  - Auto-save on blur with PATCH only when the value actually changed
 */
export const usePropertyFormField = (
  property: Property,
  propertyId: string,
  survey: DetailedSurvey | null,
) => {
  const dispatch = useAppDispatch();
  const propertyFieldRule =
    survey?.surveyPropertyFieldRule?.propertyFieldRuleList;

  const activeFieldRuleList = getActiveFieldLabelList(propertyFieldRule ?? []);

  const [updateProperty] = useUpdatePropertyMutation();
  const { updateFieldRule } = usePropertyFieldUpdate();

  // Tracks only the currently focused field's uncommitted value.
  // Replaces the unused full-property local copy and the clearedDashFields Set.
  const [focusedField, setFocusedField] = useState<FocusedField | null>(null);

  const getFieldValue = (
    propValue: string | undefined,
    field: string,
  ): string => {
    if (focusedField?.key === field) return focusedField.value;
    return propValue ?? "";
  };

  const handleFocus = (propValue: string | undefined, field: string) => {
    setFocusedField({
      key: field,
      // Clear the em-dash placeholder so the user can type immediately.
      value: propValue === "\u2014" ? "" : (propValue ?? ""),
    });
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string,
  ) => {
    setFocusedField({ key: field, value: e.target.value });
  };

  const handleBlur = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string,
    fieldLabel?: string,
  ) => {
    const updatedValue = e.target.value.trim();
    const originalValue = property?.[field as keyof Property] as string;

    setFocusedField(null);

    // If the user focused a "—" field without entering a value, skip the PATCH.
    if (updatedValue === "" && originalValue === "\u2014") {
      return;
    }

    if (updatedValue !== originalValue) {
      dispatch(
        updatePropertyAction({
          propertyId,
          updates: { [field]: updatedValue },
        }),
      );
      updateProperty({
        propertyId,
        updates: { [field]: updatedValue },
      });

      if (updatedValue) {
        const matchingFieldRule = propertyFieldRule?.find(
          (rule) =>
            rule.fieldKey === (fieldLabel ?? field) ||
            rule.fieldLabel === (fieldLabel ?? field) ||
            rule.fieldKey?.toLowerCase() ===
              (fieldLabel ?? field).toLowerCase() ||
            rule.fieldLabel?.toLowerCase() ===
              (fieldLabel ?? field).toLowerCase(),
        );

        if (matchingFieldRule?.isHidden) {
          dispatch(
            updatePropertyFieldRule({
              fieldId: matchingFieldRule.fieldId,
              isHidden: false,
              status: matchingFieldRule.status,
              order: activeFieldRuleList.length, // Set order to the end of the active list
            }),
          );
        }

        updateFieldRule(fieldLabel ?? field, updatedValue);
      }
    }
  };

  return {
    activeFieldRuleList,
    getFieldValue,
    handleFocus,
    handleInputChange,
    handleBlur,
  };
};
